import { TickLayerName } from "../config/tickLayers";
import { Persistence } from "../config/constants";
import { BaseEntity } from "../core/entity/BaseEntity";
import { Entity } from "../core/entity/Entity";
import { on } from "../core/entity/handler";

/**
 * The spend-to-advance clock: the day is a budget of working minutes,
 * and how fast the clock runs depends on what the player is doing.
 *
 * Why: in a real-time game with no speed controls, "actions cost time"
 * can never be an economy — the resource spent is the player's
 * real-world patience, and the moments the game is most interesting
 * (planning, reading, arranging) get penalized because the clock runs
 * while you think. Two things fix both problems at once: the clock's
 * pace follows what the player is doing (thinking is nearly free, work
 * costs minutes), and the day ends with the drive home (a budget with a
 * deliberate close, not a metronome).
 *
 *  waiting — the wait key is held: time deliberately spent on nothing.
 *            The rate ramps up the longer the hold (WAIT_START_PACE to
 *            WAIT_MAX_PACE over WAIT_RAMP_SECONDS), topping out past
 *            working pace. The easy answer to a cure; every hour waited
 *            is an hour not worked.
 *  working — time is being spent: attended machine work, a held
 *            cleaning tool, a scavenging run's search. Full pace, the
 *            familiar five minutes a second.
 *  idle    — nobody is spending time. The clock still creeps at about
 *            five times real life (a couple of real hours to idle away
 *            a whole day): thinking is nearly free.
 *  stopped — the shop is closed for the night, the player is home in
 *            bed, or a trip's curtain is down. Nothing moves until work
 *            finishes it or morning does.
 *
 * Machines consume time, they never generate it: hands-free phases
 * (glue curing) advance whenever minutes flow but cause none themselves
 * — a cure finishes on the minutes something else spends. Wait is the
 * easy answer to a cure and the game never punishes it, but every hour
 * waited is an hour not worked, so the skilled play that emerges is
 * filling cures with other work, or gluing up at day's end and letting
 * the overnight do it.
 *
 * The participants come to TimeFlow:
 *  - Entities that consume time register a *spender* — a callback that
 *    returns true while they're actively spending. Any true spender puts
 *    the clock at working pace. A spender that `yieldsToWait` ranks
 *    below the wait key instead: a scripted beat that should pass in
 *    seconds rather than at the idle creep (the dealt first buyer's
 *    walk-up), but that a held wait may still spin past.
 *  - The player's held wait key and the night state are provided by
 *    their owners via the provider setters.
 *  - Whatever holds the world still — the player home in bed, a trip's
 *    curtain while the truck rolls — registers a *stop* the same way a
 *    spender registers, and any true stop freezes the clock.
 *
 * Each engine tick, TimeFlow resolves the speed — stopped > working >
 * waiting > a yielding spender > night-stop > idle — and converts the
 * engine's real dt into `gameDt`, the shop minutes the simulation
 * advances this tick. Every sim system draws its work down by `gameDt`
 * (its arithmetic must be right for any size of step; the street
 * substeps long ones itself), while the body, camera, and UI keep
 * consuming raw dt. Minutes can also be *forced* outside the pace model
 * — the driver's `tick(n)`, a drive's charged leg, the overnight — and
 * are served whole, in one engine tick.
 *
 * The rest of the system, where it lives:
 *  - src/game/time.ts — the day's units (600 working minutes, 840
 *    overnight, 1440 to a calendar day), its phases, and the night
 *    question. Everything the game quotes "in days" is denominated in
 *    calendar days, so "three days" means three mornings from now.
 *  - src/sim/singletons/Clock.ts — the minute counter itself.
 *  - src/sim/systems/MilestoneSystem.ts — the action-answering cadence
 *    (milestone unlocks) that runs regardless of clock pace.
 *  - src/game/game-actions/door-actions.ts — trips charging for the
 *    drive; src/sim/systems/SleepSystem.ts — the overnight as one batch.
 *  - src/game/calendar.ts — the derived, presentation-only date.
 *  - src/components/DayDial.tsx — the day told by its light; there is
 *    deliberately no wall clock.
 *  - src/game/daylight.ts — where the sun is, which the dial and the lit
 *    lot both read so they can never disagree.
 *  - src/sim/sequences/day-loop.test.ts — the day loop's promises.
 *
 * TimeFlow runs on the "input" tick layer so every sim layer of the same
 * engine tick sees one consistent gameDt.
 */
export type TimeSpeed = "waiting" | "working" | "idle" | "stopped";

/**
 * Working pace, in game minutes per real second — the familiar five
 * minutes a second.
 */
export const WORKING_PACE = 5;

/**
 * The idle creep: about five times real life, so a full day of pure
 * idling takes around two hours. Thinking is nearly free.
 */
export const IDLE_PACE = 5 / 60;

/**
 * The wait key's ramp. Holding it starts the clock at a gentle spin and
 * winds it up over a few seconds to twice working pace.
 */
export const WAIT_START_PACE = 2;
export const WAIT_MAX_PACE = 2 * WORKING_PACE;
export const WAIT_RAMP_SECONDS = 5;

/**
 * Ceiling on game minutes simulated in one paced engine tick, so a pace
 * spike (a long frame, a tab coming back) can never tunnel the sim past
 * events. At 60 engine ticks/s even full wait pace only wants ~0.17
 * minutes a tick, so the cap is slack in normal play. Forced batches
 * are served whole regardless — they are booked in minutes, not paced.
 */
export const MAX_GAME_MINUTES_PER_TICK = 1;

type BoolProvider = () => boolean;

export interface SpenderOptions {
  /**
   * Rank below the held wait key rather than above it: the clock runs
   * at working pace for this spender only while nothing else is
   * spending and the player isn't deliberately waiting.
   */
  readonly yieldsToWait?: boolean;
}

export class TimeFlow extends BaseEntity implements Entity {
  id = "timeFlow";
  tickLayer: TickLayerName = "input";
  // Pace state is session-transient, never saved: it survives scene swaps
  // AND save loads (which clear at Persistence.Game).
  persistenceLevel: number = Persistence.Permanent;
  pausable = true;

  /**
   * The shop minutes the sim advances this engine tick — the slice of a
   * minute the pace brings, or a forced batch served whole.
   */
  gameDt = 0;
  /** The speed resolved for this engine tick. */
  speed: TimeSpeed = "idle";

  /** Minutes queued by forceMinutes, drained on the next engine tick. */
  private forcedMinutes = 0;

  private spenders = new Set<BoolProvider>();
  private yieldingSpenders = new Set<BoolProvider>();
  private stops = new Set<BoolProvider>();
  private waitingProvider: BoolProvider = () => false;
  private nightProvider: BoolProvider = () => false;
  private waitHeldSeconds = 0;

  /**
   * Register a callback that returns true while its owner is actively
   * consuming the player's time. Returns an unregister function; entities
   * should call it from onDestroy.
   */
  registerSpender(
    spender: BoolProvider,
    options: SpenderOptions = {},
  ): () => void {
    const set = options.yieldsToWait ? this.yieldingSpenders : this.spenders;
    set.add(spender);
    return () => set.delete(spender);
  }

  /** The player's held wait key. */
  setWaitingProvider(provider: BoolProvider): void {
    this.waitingProvider = provider;
  }

  /** Whether the day's budget is spent (the shop is closed for the night). */
  setNightProvider(provider: BoolProvider): void {
    this.nightProvider = provider;
  }

  /**
   * Register a callback that returns true while its owner is holding the
   * world still — the player home in bed, where the overnight passes as
   * one batch rather than a stream of live ticks, or a trip's curtain
   * while the truck rolls out of the driveway and back in. A stop
   * outranks every other pace. Returns an unregister function.
   */
  registerStop(stop: BoolProvider): () => void {
    this.stops.add(stop);
    return () => this.stops.delete(stop);
  }

  /**
   * Resolve how fast the clock should run right now: a hard stop wins;
   * any spender means working (so a held wait key can never speed up an
   * attended cut); while there's day left, the wait key spins the clock
   * and a yielding spender runs it at working pace; otherwise night
   * stops the clock and daytime creeps.
   */
  resolveSpeed(): TimeSpeed {
    for (const stop of this.stops) {
      if (stop()) {
        return "stopped";
      }
    }
    for (const spender of this.spenders) {
      if (spender()) {
        return "working";
      }
    }
    const night = this.nightProvider();
    if (!night) {
      if (this.waitingProvider()) {
        return "waiting";
      }
      for (const spender of this.yieldingSpenders) {
        if (spender()) {
          return "working";
        }
      }
    }
    return night ? "stopped" : "idle";
  }

  /** The wait pace after the key has been held this long. */
  static waitPace(heldSeconds: number): number {
    const t = Math.min(1, Math.max(0, heldSeconds / WAIT_RAMP_SECONDS));
    return WAIT_START_PACE + t * (WAIT_MAX_PACE - WAIT_START_PACE);
  }

  /**
   * Minutes queued by forceMinutes that no tick has served yet — zero
   * means any charged leg (a store drive, say) has already run, which is
   * what the shell's deferred trip completions wait on.
   */
  get pendingForcedMinutes(): number {
    return this.forcedMinutes;
  }

  /**
   * Queue shop minutes to run on the next engine tick, outside the pace
   * model. The sequence driver's `tick(n)`, a drive's charged leg, and
   * the overnight batch advance the world this way — served whole, in
   * one engine tick, to every sim system.
   */
  forceMinutes(minutes: number): void {
    this.forcedMinutes += minutes;
  }

  @on("tick")
  onTick(dt: number) {
    this.speed = this.resolveSpeed();

    // The ramp winds up only while the wait key is what's driving the
    // clock, and resets whenever anything else takes over.
    if (this.speed === "waiting") {
      this.waitHeldSeconds += dt;
    } else {
      this.waitHeldSeconds = 0;
    }

    // A forced engine tick serves exactly the forced minutes: the pace
    // model neither accrues nor advances alongside it, so the driver's
    // tick(n) (and the overnight batch) mean exactly n minutes — never
    // n plus whatever the live pace would have crept in.
    if (this.forcedMinutes > 0) {
      this.gameDt = this.forcedMinutes;
      this.forcedMinutes = 0;
      return;
    }

    const pace =
      this.speed === "working"
        ? WORKING_PACE
        : this.speed === "idle"
          ? IDLE_PACE
          : this.speed === "waiting"
            ? TimeFlow.waitPace(this.waitHeldSeconds)
            : 0;

    this.gameDt = Math.min(pace * dt, MAX_GAME_MINUTES_PER_TICK);
  }
}
